import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

/**
 * A tick-box dropdown — the light, frosted menu the rest of the app uses, but
 * multi-select: the menu STAYS OPEN while you tick, because picking three
 * workspaces should not mean opening the same menu three times.
 *
 * Each row carries a real tick-box on the left, so an unticked row still shows
 * where the tick will land. A bare check mark that only exists once selected
 * gives no sign the row is selectable at all.
 *
 * It stays a dropdown rather than an inline column of checkboxes: with a dozen
 * workspaces that column pushes every other setting off the panel.
 */
const MultiSelect = ({ options, selected, onToggle, emptyLabel }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const chosen = options.filter((o) => selected.includes(o.value));
  let label = emptyLabel;
  if (chosen.length === options.length && options.length) label = "All workspaces";
  else if (chosen.length > 2) label = `${chosen.length} workspaces`;
  else if (chosen.length) label = chosen.map((o) => o.label).join(", ");

  return (
    <div className={`gmbw-ms${open ? " open" : ""}`} ref={ref}>
      <button type="button" className="gmbw-ms-b" onClick={() => setOpen((v) => !v)}>
        <span className="gmbw-ms-v">{label}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="gmbw-ms-m" role="listbox">
          {options.map((o) => {
            const on = selected.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={on}
                className={`gmbw-ms-i${on ? " on" : ""}`}
                onClick={() => onToggle(o.value)}
              >
                <span className="gmbw-ms-box" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                <span className="gmbw-ms-lb">{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

MultiSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  emptyLabel: PropTypes.string,
};
MultiSelect.defaultProps = {
  emptyLabel: "Pick workspaces",
};

export default MultiSelect;
